/**
 * ScorerChat — floating in-app assistant on the ball-by-ball scoring page.
 *
 * Phase 1: read-only Q&A ("what was the last wicket", "how many balls
 *          has Bumrah bowled", "who's on strike").
 * Phase 2: proposes mutations (undo / edit) — confirmation card before
 *          we actually call the existing mutation API.
 * Phase 3: admin-only — proposes "rebuild innings" for stats-mismatch fixes.
 *
 * The backend never mutates from the chat endpoint. It returns either a
 * plain reply or a `pendingActions` array. Each action is shown as a
 * Confirm/Cancel card; on confirm the frontend calls the same APIs the
 * EditBallDialog and undo-button already use, so audit-log + permission
 * checks stay in their existing pathways.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { X, Send, Bot, User as UserIcon, Check, AlertTriangle, Sparkles, ArrowLeft } from "lucide-react";
import api from "../../api/axios";
import { undoBall as apiUndoBall, updateBall as apiUpdateBall, restoreBallFromTrash as apiRestoreBallFromTrash } from "../../api/ballbyball.api";
import { emit as emitWalletEvent } from "../../lib/walletEvents";
import { useTranslation } from "../../i18n/useTranslation";
import useIsMobile from "../../hooks/useIsMobile";
import useWalletStore from "../../store/wallet.store";
import useAuthStore from "../../store/auth.store";
import useDialogDismiss from "../../hooks/useDialogDismiss";

// 2026-06-06 — storage keys are USER-SCOPED. The earlier unscoped keys
// (zcd_scorer_chat_history, zcd_scorer_chat_open, zcd_scorer_chat_model)
// leaked one operator's chat history into the next operator's session
// on a shared device — user A's questions + the assistant's replies
// were rendered as soon as user B opened the panel, before the API
// call returned the (correct, user-B-scoped) server history. Per-user
// keys close that gap. We also wipe the legacy unscoped keys on first
// render so they stop sitting in browsers forever.
const LEGACY_KEY_HISTORY = "zcd_scorer_chat_history";
const LEGACY_KEY_OPEN = "zcd_scorer_chat_open";
const LEGACY_KEY_MODEL = "zcd_scorer_chat_model";

function userScopedKey(base, userId) {
  // Anonymous fallback isolates the keys to a single bucket — the chat
  // is auth-gated downstream, so this branch is mostly unreachable.
  return userId ? `${base}_${userId}` : `${base}_anon`;
}

export default function ScorerChat({ matchId, onAfterMutation }) {
  const { t, lang } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? null;

  const STORAGE_KEY_HISTORY = useMemo(
    () => userScopedKey("zcd_scorer_chat_history", userId), [userId],
  );
  const STORAGE_KEY_OPEN = useMemo(
    () => userScopedKey("zcd_scorer_chat_open", userId), [userId],
  );
  const STORAGE_KEY_MODEL = useMemo(
    () => userScopedKey("zcd_scorer_chat_model", userId), [userId],
  );

  // Wipe the legacy unscoped keys ONCE on first mount so any chat
  // history left over from before this fix shipped doesn't keep
  // leaking on shared devices.
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_KEY_HISTORY);
      localStorage.removeItem(LEGACY_KEY_OPEN);
      localStorage.removeItem(LEGACY_KEY_MODEL);
    } catch { /* ignore */ }
  }, []);

  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(userScopedKey("zcd_scorer_chat_open", userId)) === "1"; } catch { return false; }
  });
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(userScopedKey("zcd_scorer_chat_history", userId));
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [model, setModel] = useState(() => {
    try { return localStorage.getItem(userScopedKey("zcd_scorer_chat_model", userId)) === "sonnet" ? "sonnet" : "haiku"; }
    catch { return "haiku"; }
  });

  // When the auth user changes (logout → login as different user on
  // the same device), drop the previous user's in-memory chat
  // immediately so the panel isn't briefly populated with their data
  // before the per-user localStorage read kicks in.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_HISTORY);
      setMessages(raw ? JSON.parse(raw) : []);
      setOpen(localStorage.getItem(STORAGE_KEY_OPEN) === "1");
      setModel(localStorage.getItem(STORAGE_KEY_MODEL) === "sonnet" ? "sonnet" : "haiku");
    } catch {
      setMessages([]);
      setOpen(false);
      setModel("haiku");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  const scrollRef = useRef(null);
  const costPerMsg = model === "sonnet" ? 5 : 2;
  const balance = useWalletStore((s) => s.balance);
  const balanceCredits = balance?.balance_credits ?? null;
  const canAfford = balanceCredits == null || balanceCredits >= costPerMsg;
  // Mobile + tablet (<=1023px) gets a fullscreen layout per the
  // redesign roadmap; the floating-bottom-right panel doesn't work on
  // phones — composer is unreachable behind the soft keyboard, and the
  // panel covers the score ribbon. Fullscreen with sticky header +
  // composer fixes both. 1024 cutoff matches the sidebar-drawer lg:
  // breakpoint so the two surfaces agree on what "mobile" means
  // (2026-05-27 audit fix).
  const isMobile = useIsMobile(1024);

  // Persist — keyed by current user, so a second operator on the same
  // browser doesn't read another operator's saved chat.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_HISTORY, JSON.stringify(messages.slice(-30))); } catch { /* ignore */ }
  }, [messages, STORAGE_KEY_HISTORY]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_OPEN, open ? "1" : "0"); } catch { /* ignore */ }
  }, [open, STORAGE_KEY_OPEN]);

  // Auto-scroll to latest
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  // Allow other parts of the app (e.g. the integrity badge) to open the chat
  // pre-loaded with a question.
  useEffect(() => {
    const onPrompt = (e) => {
      const text = e?.detail?.text || "";
      if (!text) return;
      setOpen(true);
      setInput(text);
    };
    window.addEventListener("zcd:chat-prompt", onPrompt);
    return () => window.removeEventListener("zcd:chat-prompt", onPrompt);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setError("");
    setLoading(true);
    try {
      const endpoint = model === "sonnet"
        ? "/api/scoring/ai/scorer-chat/sonnet/"
        : "/api/scoring/ai/scorer-chat/";
      const r = await api.post(endpoint, {
        match_id: matchId,
        message: text,
        history: next.slice(-12).map((m) => ({ role: m.role, content: m.content })),
        language: lang,
      });
      const reply = r.data?.reply || "(no response)";
      const pending = r.data?.pendingActions || [];
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply, pendingActions: pending },
      ]);
      // Backend debits 2 ZC (Haiku) or 5 ZC (Sonnet) per message in
      // CreditGatedMixin.initial() before this handler returns. Without
      // emitting, the header badge stays on a stale balance and operators
      // think credits weren't deducted. Mirrors useCreditConfirm.js.
      emitWalletEvent("wallet_changed");
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.detail || e.message || "Failed";
      setError(msg);
      setMessages((prev) => [...prev, { role: "assistant", content: `Sorry — ${msg}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleConfirm = async (action, msgIdx) => {
    setError("");
    try {
      let summary = "";
      if (action.kind === "undo_last_ball") {
        await apiUndoBall(matchId);
        summary = "Undone the last ball.";
      } else if (action.kind === "redo_last_ball") {
        // Server resolved trash_id at proposal time; replay through the
        // same trash-restore primitive the coding-screen redo button uses.
        await apiRestoreBallFromTrash(action.trash_id);
        summary = "Restored the last undone ball.";
      } else if (action.kind === "edit_ball") {
        const payload = mapEditFieldsToBallPayload(action.edits || {});
        // Defence-in-depth: BallInputSerializer silently drops undeclared
        // fields and returns 200 with no change. If the mapper produced
        // nothing the AI proposed must have used an unsupported key —
        // surface an error rather than lie about success.
        if (Object.keys(payload).length === 0) {
          throw new Error(
            "I drafted an edit but none of the proposed fields are supported by " +
            "the chat editor. Use the Edit Ball dialog (over-history strip → click " +
            "the square) for full-control edits."
          );
        }
        await apiUpdateBall(action.ball_id, payload);
        summary = "Ball updated.";
      } else if (action.kind === "rebuild_innings") {
        await api.post("/api/scoring/ai/scorer-chat/rebuild-innings/", {
          innings_id: action.innings_id,
        });
        summary = "Innings stats rebuilt from ball rows.";
      } else {
        summary = `Unknown action ${action.kind}`;
      }
      setMessages((prev) => prev.map((m, i) =>
        i === msgIdx
          ? { ...m, pendingActions: [], confirmed: [...(m.confirmed || []), { ...action, summary }] }
          : m
      ));
      onAfterMutation?.(action);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || e.message || "Action failed");
    }
  };

  const handleCancel = (msgIdx) => {
    setMessages((prev) => prev.map((m, i) =>
      i === msgIdx ? { ...m, pendingActions: [], cancelled: true } : m
    ));
  };

  const clearHistory = () => {
    setMessages([]);
    try { localStorage.removeItem(STORAGE_KEY_HISTORY); } catch { /* ignore */ }
  };

  // 2026-06-10 — Esc + hardware-back close. Hooks MUST be declared
  // before the `if (!open) return` early return below, otherwise the
  // hook call count differs between renders and React throws error #310
  // ("Rendered fewer hooks than expected"). Moved up from after the
  // early return where it lived previously.
  const containerRef = useRef(null);
  useDialogDismiss({
    open,
    onClose: () => setOpen(false),
    ref: containerRef,
    focusTrap: isMobile,
    scrollLock: isMobile,
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed right-3 z-50 flex items-center gap-2 px-3 py-2 rounded-full bg-button-primary text-white shadow-lg hover:brightness-110 transition-all"
        style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
        title="Ask the scoring assistant"
      >
        <Bot className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wider">Ask</span>
      </button>
    );
  }

  // Mobile fullscreen layout: app-bar (back + title + actions) → scrollable
  // body → sticky composer. Locks scroll on the body behind it so the page
  // underneath doesn't bleed-scroll when the user swipes inside the chat.
  const containerClass = isMobile
    ? "fixed inset-0 z-[65] bg-bg-primary flex flex-col"
    : "fixed right-3 z-50 w-[min(92vw,380px)] h-[min(70vh,560px)] bg-bg-primary border border-border-default rounded-xl shadow-2xl flex flex-col overflow-hidden";
  const containerStyle = isMobile
    ? undefined
    : { bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" };

  return (
    <div
      ref={containerRef}
      role={isMobile ? "dialog" : undefined}
      aria-modal={isMobile ? "true" : undefined}
      aria-label="Scoring assistant"
      className={containerClass}
      style={containerStyle}
    >
      <div className={`flex items-center justify-between gap-2 px-3 py-2 bg-bg-secondary border-b border-border-default ${isMobile ? "sticky top-0" : ""}`}>
        <div className="flex items-center gap-2 min-w-0">
          {isMobile && (
            <button
              onClick={() => setOpen(false)}
              className="p-1 -ml-1 rounded text-text-secondary hover:text-text-primary"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <Bot className="w-4 h-4 text-button-primary shrink-0" />
          <span className="text-sm font-semibold text-text-primary">Scoring assistant</span>
          <span
            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-mono shrink-0 ${
              canAfford
                ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                : "bg-rose-500/15 text-rose-300 border-rose-500/30"
            }`}
            title={balanceCredits != null
              ? `Each message costs ${costPerMsg} Zencredits. You have ${balanceCredits} ZC.`
              : `Each message costs ${costPerMsg} Zencredits.`}
          >
            {costPerMsg} ZC/msg{balanceCredits != null ? ` · bal ${balanceCredits}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Haiku ↔ Sonnet escalation. Sonnet for rule interpretation /
              multi-step reasoning; Haiku is fine for plain Q&A.        */}
          <button
            onClick={() => {
              const next = model === "sonnet" ? "haiku" : "sonnet";
              setModel(next);
              try { localStorage.setItem(STORAGE_KEY_MODEL, next); } catch {/* ignore */}
            }}
            title={
              model === "sonnet"
                ? "Sonnet mode (5 ZC/msg) — deeper reasoning. Click to switch back to Haiku."
                : "Haiku mode (2 ZC/msg) — fast & cheap. Click to escalate to Sonnet for hard questions."
            }
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border inline-flex items-center gap-1 ${
              model === "sonnet"
                ? "bg-violet-500/15 text-violet-300 border-violet-500/40"
                : "bg-bg-primary text-text-secondary border-border-default hover:text-text-primary"
            }`}
          >
            <Sparkles className="w-3 h-3" />
            {model === "sonnet" ? "Sonnet" : "Haiku"}
          </button>
          {messages.length > 0 && (
            <button
              onClick={clearHistory}
              className="text-[10px] uppercase tracking-wider text-text-secondary hover:text-text-primary px-2"
              title="Clear chat history"
            >
              Clear
            </button>
          )}
          {!isMobile && (
            <button
              onClick={() => setOpen(false)}
              className="text-text-secondary hover:text-text-primary p-1"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 text-sm">
        {messages.length === 0 && (
          <div className="text-text-secondary text-xs">
            <p className="mb-2">Ask in plain English. Examples:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>"what's the score"</li>
              <li>"who's on strike"</li>
              <li>"undo last ball"</li>
              <li>"change ball 4.3 to 4 runs"</li>
              <li>"the wicket on 8.4 was a stumping not caught"</li>
              <li className="text-amber-300">"the over count looks wrong, fix it" (admin)</li>
            </ul>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && <Bot className="w-4 h-4 text-button-primary shrink-0 mt-1" />}
            <div className="max-w-[85%]">
              <div
                className={`rounded-lg px-3 py-2 ${
                  m.role === "user"
                    ? "bg-button-primary text-white"
                    : m.error
                    ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                    : "bg-bg-secondary text-text-primary"
                }`}
              >
                <p className="whitespace-pre-wrap">{m.content}</p>
              </div>

              {m.pendingActions?.length > 0 && (
                <div className="mt-2 space-y-2">
                  {m.pendingActions.map((a, ai) => (
                    <div key={ai} className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-2">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                        <div className="text-xs">
                          <p className="font-semibold text-amber-300">Confirm action</p>
                          <p className="text-text-secondary mt-0.5">{a.label}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleConfirm(a, i)}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30"
                        >
                          <Check className="w-3 h-3" />
                          Confirm
                        </button>
                        <button
                          onClick={() => handleCancel(i)}
                          className="flex-1 px-2 py-1 text-[11px] uppercase tracking-wider rounded bg-bg-primary border border-border-default text-text-secondary hover:text-text-primary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {m.confirmed?.length > 0 && (
                <div className="mt-1 text-[11px] text-emerald-400">
                  {m.confirmed.map((c, ci) => <p key={ci}>✓ {c.summary}</p>)}
                </div>
              )}
              {m.cancelled && (
                <p className="mt-1 text-[11px] text-text-secondary">Cancelled.</p>
              )}
            </div>
            {m.role === "user" && <UserIcon className="w-4 h-4 text-text-secondary shrink-0 mt-1" />}
          </div>
        ))}

        {loading && (
          <div className="flex items-center gap-2 text-text-secondary text-xs">
            <Bot className="w-4 h-4 text-button-primary" />
            <span className="animate-pulse">thinking…</span>
          </div>
        )}
      </div>

      {error && (
        <div className="px-3 py-1.5 text-[11px] text-rose-300 bg-rose-500/10 border-t border-rose-500/30">
          {error}
        </div>
      )}

      {/* Sticky composer (mobile only). One sticky wrapper holds both
          the input row and the cost line, so the soft-keyboard can't
          push either off-screen and the two don't double-stack.
          paddingBottom: env(safe-area-inset-bottom) keeps the send
          button above the iOS home-indicator bar. */}
      <div
        className={`bg-bg-primary border-t border-border-default ${isMobile ? "sticky bottom-0" : ""}`}
        style={isMobile ? { paddingBottom: "env(safe-area-inset-bottom, 0px)" } : undefined}
      >
        <div className="p-2 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={loading}
            placeholder="Ask anything…"
            className="flex-1 bg-bg-secondary text-text-primary text-sm px-3 py-2 rounded border border-border-default focus:outline-none focus:border-button-primary disabled:opacity-50"
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            className="px-3 py-2 rounded bg-button-primary text-white disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        {/* Cost line — surfaces the per-message cost inline; insufficient
            credits is handled by the global 402 modal. */}
        <div className="px-3 pb-1.5 text-[10px] text-text-secondary text-right">
          {costPerMsg} Zencredits per message
        </div>
      </div>
    </div>
  );
}

// Map the AI's edit-ball field names to the payload shape the
// `PATCH /api/scoring/balls/<id>/edit/` endpoint actually validates
// (BallInputSerializer in apps/scoring/serializers/ball_input.py).
//
// Gotchas the previous version got wrong, which silently no-op'd every
// edit (DRF Serializer ignores undeclared fields, returning 200 with no
// change so the chat said "Ball updated" but the DB stayed put):
//   - runs.* must live inside a nested `runs` DictField, not at the
//     top level. The serializer rejects top-level runs_off_bat /
//     completed_runs / wide_runs / etc.
//   - The Ball model has NO top-level is_wide / is_no_ball / is_bye /
//     is_leg_bye field — those are derived from runs.wide_runs / etc.
//     "Mark this ball as a wide" maps to runs.wide_runs = 1; unmark
//     maps to 0. Same for no_ball / bye / leg_bye.
//   - is_free_hit DOES exist as a Ball field, but the serializer
//     reads it from runs.is_free_hit, not the top level.
//   - The wicket sub-key is `wicket_type`, not `type`.
//   - Commentary is stamped on Ball.auto_commentary at the top level
//     (not `commentary`).
function mapEditFieldsToBallPayload(edits) {
  const payload = {};
  const runs = {};

  if (edits.runs_off_bat !== undefined) {
    const n = Number(edits.runs_off_bat);
    runs.runs_off_bat = n;
    runs.completed_runs = n;
  }
  if (edits.is_wide !== undefined) {
    runs.wide_runs = edits.is_wide ? 1 : 0;
  }
  if (edits.is_no_ball !== undefined) {
    runs.no_ball_runs = edits.is_no_ball ? 1 : 0;
  }
  if (edits.is_bye !== undefined) {
    runs.bye_runs = edits.is_bye ? 1 : 0;
  }
  if (edits.is_leg_bye !== undefined) {
    runs.leg_bye_runs = edits.is_leg_bye ? 1 : 0;
  }
  if (edits.is_free_hit !== undefined) {
    runs.is_free_hit = Boolean(edits.is_free_hit);
  }
  if (Object.keys(runs).length > 0) {
    payload.runs = runs;
  }

  if (edits.wicket_type !== undefined) {
    payload.wicket = edits.wicket_type
      ? { wicket_type: edits.wicket_type }
      : null;
  }
  if (edits.commentary !== undefined) {
    payload.auto_commentary = String(edits.commentary).slice(0, 500);
  }
  return payload;
}
